/**
 * One message at the top of the screen after an action («Об'єкт підключено…»,
 * «Зміни збережено…»). Survives the navigation that follows the action and
 * goes with the next one; closed by «×» or replaced by the next message.
 * The provider sits inside the router and outlives the pages (each page
 * mounts its own shell), so it is the one that sees the navigation.
 */
import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';

import { useT } from '../i18n/locale-context.js';

interface Ctx {
  text: string | null;
  show: (text: string) => void;
  hide: () => void;
}

const ToastCtx = createContext<Ctx | null>(null);

/** A navigation within this time after the message is the one the action made. */
const FOLLOW_MS = 1500;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [text, setText] = useState<string | null>(null);
  const shownAt = useRef(0);
  const { pathname } = useLocation();
  const lastPath = useRef(pathname);

  useEffect(() => {
    if (pathname === lastPath.current) return;
    lastPath.current = pathname;
    if (Date.now() - shownAt.current > FOLLOW_MS) setText(null);
  }, [pathname]);

  const show = useCallback((t: string) => {
    shownAt.current = Date.now();
    setText(t);
    window.scrollTo(0, 0);
  }, []);
  const hide = useCallback(() => setText(null), []);
  const value = useMemo(() => ({ text, show, hide }), [text, show, hide]);
  return <ToastCtx.Provider value={value}>{children}</ToastCtx.Provider>;
}

export function useToast(): Ctx {
  const v = useContext(ToastCtx);
  if (!v) throw new Error('useToast must be used within ToastProvider');
  return v;
}

export function Toast() {
  const t = useT();
  const { text, hide } = useToast();
  if (!text) return null;
  return (
    <div className="toast" aria-live="polite">
      <span>{text}</span>
      <button type="button" aria-label={t('common.close')} onClick={hide}>
        ×
      </button>
    </div>
  );
}
